// Partner-scoped localStorage helpers.
//
// All persisted browser state (pinned tabs, favorites, recent searches, saved
// pipeline layouts/filters, table column state, saved id maps, etc.) must be
// namespaced by the active partner so that two partner builds served from the
// same origin during local development (e.g. both on http://localhost:3000)
// do NOT read/write each other's state.
//
// The namespace is derived from REACT_APP_PARTNER_NAME (e.g. "eastview",
// "lendmarq"). We intentionally do NOT monkey-patch window.localStorage so that
// third-party storage (Stytch, etc.) is left untouched — only keys that flow
// through nsKey()/partnerStorage are scoped.

export const STORAGE_NAMESPACE = process.env.REACT_APP_PARTNER_NAME || "app";

/**
 * Prefix a localStorage key with the active partner namespace.
 * Idempotent: a key that is already namespaced is returned unchanged.
 *
 * @param {string} key - raw storage key
 * @returns {string} namespaced key, e.g. "eastview::loanRecentSearches"
 */
export const nsKey = (key) => {
  if (key == null) return key;
  const prefix = `${STORAGE_NAMESPACE}::`;
  return String(key).startsWith(prefix) ? String(key) : `${prefix}${key}`;
};

/**
 * Thin localStorage wrapper that auto-namespaces every key. Use this in place
 * of `localStorage.*` for app-owned values. API mirrors the native Storage
 * methods so call sites only need the object swapped in.
 */
export const partnerStorage = {
  getItem: (key) => localStorage.getItem(nsKey(key)),
  setItem: (key, value) => localStorage.setItem(nsKey(key), value),
  removeItem: (key) => localStorage.removeItem(nsKey(key)),
};

// The full prefix every app-owned key carries, e.g. "eastview::".
export const PARTNER_PREFIX = `${STORAGE_NAMESPACE}::`;

/**
 * Enumerate every app-owned (partner-namespaced) localStorage entry. Only keys
 * that flow through nsKey()/partnerStorage are returned — third-party storage
 * (Stytch session tokens, etc.) is never namespaced and so is never listed,
 * which is what makes a bulk clear safe. Each entry:
 *   { fullKey, key, value, sizeBytes }
 * where `key` is the raw (de-namespaced) key and `sizeBytes` is a rough
 * character-count of the stored key+value.
 */
export const listPartnerStorageEntries = () => {
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const fullKey = localStorage.key(i);
    if (!fullKey || !fullKey.startsWith(PARTNER_PREFIX)) continue;
    const value = localStorage.getItem(fullKey) ?? "";
    out.push({
      fullKey,
      key: fullKey.slice(PARTNER_PREFIX.length),
      value,
      sizeBytes: fullKey.length + value.length,
    });
  }
  return out;
};

/**
 * Remove every app-owned (partner-namespaced) localStorage entry, leaving
 * third-party storage untouched. Returns the number of keys removed.
 */
export const clearAllPartnerStorage = () => {
  const entries = listPartnerStorageEntries();
  entries.forEach((e) => localStorage.removeItem(e.fullKey));
  return entries.length;
};

// Whether the user has ever completed a sign-in on this device (partner-scoped).
// Stamped at the auth chokepoint (AuthProvider.login) so it covers every login
// path — email-code, password, and reset-link auto-login. Used to hide
// first-time onboarding prompts (e.g. "set up a password") for returning users.
const SIGNED_IN_BEFORE_KEY = "auth.hasSignedInBefore";
export const markSignedInBefore = () =>
  partnerStorage.setItem(SIGNED_IN_BEFORE_KEY, "true");
export const hasSignedInBefore = () =>
  partnerStorage.getItem(SIGNED_IN_BEFORE_KEY) === "true";
